import { useDraggable } from '@dnd-kit/react';
import { ActionIcon, Avatar, HoverCard, Loader, LoadingOverlay, Text, Tooltip } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconChevronDown,
  IconCircleKeyFilled,
  IconHistory,
  IconSparkles,
  IconStrikethrough,
  IconUser,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { useState, type MouseEvent } from 'react';
import { twMerge } from 'tailwind-merge';
import { erroredOperationAtom, stepSearchBarKeywordAtom } from '~/atoms/no-code-atoms';
import { DEFAULT_AUTO_HEAL_CYCLE_COUNT } from '~/common/ai/constants';
import { CollapseUnmount } from '~/components/common/collapse/CollapseUnmount';
import { isStepOptimistic, isStepSeparating } from '~/hooks/mutations/optimistic-handles';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';
import OperationListStep from './details/OperationListStep';
import StepAccountDetails from './StepAccountDetails';
import { StepPreviewButton } from './StepPreviewButton';
import StepSettingsMenuButton from './StepSettingsMenuButton';
import StepStatusAndSelect from './StepStatusAndSelect';
import StepTitle from './StepTitle';
import IconAIEdit from '~/components/icons/IconAIEdit';

const NEW_STEP_HIGHLIGHT_DURATION_MS = 1000;

export default function Step({
  step,
  className,
  isFirstNavOp,
  testContentId,
  idx,
  onClick,
  handleOperationClick,
  selected,
  readonly,
  disabled: disabledProp,
}: {
  step: SelectedStep;
  className?: string;
  testContentId: string;
  idx: number;
  // TODO: use global state for handling onClick
  onClick?: (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
  handleOperationClick?: (event: React.MouseEvent<HTMLDivElement>, operationId: string, step: SelectedStep) => void;
  selected?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  isFirstNavOp: boolean;
}) {
  const isOptimisticStep = isStepOptimistic(step.id);
  const isPolishing = isStepSeparating(step.id);
  const disabled = disabledProp || isPolishing;

  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );

  const [prevDesc, setPrevDesc] = useState<string | undefined>(undefined);

  const { hovered, ref: hoveredRef } = useHover();

  const { recorderState } = useLiveStateManager();

  // Highlight and expand newly created steps (to make it more obvious).
  const initialIsNew = step.createdAt > new Date(Date.now() - NEW_STEP_HIGHLIGHT_DURATION_MS);
  const [isNew, setIsNew] = useState(() => {
    if (initialIsNew) {
      const timeout = setTimeout(() => setIsNew(false), NEW_STEP_HIGHLIGHT_DURATION_MS);
      return () => clearTimeout(timeout);
    }
    return initialIsNew;
  });
  const [expanded, setExpanded] = useState(!isFirstNavOp && !readonly);
  const erroredOperation = useAtomValue(erroredOperationAtom);
  const stepSearchBarKeyword = useAtomValue(stepSearchBarKeywordAtom.debouncedValueAtom);

  const { handleRef, ref } = useDraggable({
    id: step.id,
    data: { type: 'moveStep', stepId: step.id },
    disabled: readonly || disabled,
  });

  const trpcUtils = api.useUtils();

  const { mutate: updateStep } = api.testStep.update.useMutation({
    onSuccess: () => {
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      trpcUtils.testContent.getCode.invalidate().catch(() => undefined);
      notifications.show({ message: 'Updated the step', color: 'blue' });
      setPrevDesc(undefined);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error saving the step', color: 'red' });
    },
  });

  // Step is before cursor if cursor position is strictly after this step
  // (allows preview to current step, but not to steps already passed)
  const stepIsBeforeCursor = recorderState?.stepOrder !== undefined && recorderState.stepOrder > step.stepOrder;

  const quickActionsDisabled =
    stepIsBeforeCursor ||
    readonly ||
    disabled ||
    step.skip ||
    (step.stepOrder === 0 && testContent?.test.testType !== 'MOBILE_APP');

  const { data: projectAutoHealConfig } = api.project.getProjectAutoHealConfig.useQuery(
    { projectId: testContent?.test.projectId },
    { enabled: !!testContent?.test.projectId },
  );
  const autoHealConfig = step.autoHealConfig ?? projectAutoHealConfig ?? 'USER_FLOW';

  const { data: projectAdminInfo } = api.project.getProjectAdminInfo.useQuery(
    {
      projectId: testContent?.test.projectId || '',
    },
    { enabled: !!testContent?.test.projectId },
  );
  const autoHealCycleLimit = projectAdminInfo?.autoHealCycleLimit ?? DEFAULT_AUTO_HEAL_CYCLE_COUNT;

  return (
    <div
      // scroll-mt-28 to account for control bar
      className={twMerge('z-30 flex scroll-mt-28 flex-row p-1', className)}
      // do not remove, used in StepToStepButton.tsx and StepList.tsx
      id={`step-${step.id}`}
      ref={hoveredRef}
    >
      {/* Step card */}
      <div className="animate-slowFadeIn relative w-full">
        <LoadingOverlay
          visible={isPolishing || isOptimisticStep}
          zIndex={10}
          overlayProps={{ radius: 'sm', blur: 1, backgroundOpacity: 0.2 }}
          loaderProps={{
            children: (
              <div className="flex items-center space-x-2 rounded-xl bg-white px-4 py-4 text-blue-500 shadow-2xl dark:bg-neutral-800 dark:shadow-white/10">
                {isPolishing ?
                  <>
                    <IconSparkles
                      size={25}
                      className="animate-spin"
                    />
                    <div className="animate-pulse text-sm font-bold">Grouping into steps...</div>
                  </>
                : <>
                    <Loader
                      className="justify-center self-center"
                      color="blue"
                      size={25}
                    />
                    <div className="animate-pulse text-sm font-bold">Creating step...</div>
                  </>
                }
              </div>
            ),
          }}
        />
        <div
          ref={ref}
          onClick={disabled ? undefined : onClick}
          className={clsx(
            'flex-grow rounded-md border-[2px] bg-white transition-colors duration-200 ease-in-out dark:bg-stone-800',
            isNew ? 'border-blue-300 shadow-[0_0_10px_rgba(96,165,250,0.3)]' : 'dark:border-gray-700',
            {
              'border-blue-200 dark:border-blue-200': hovered && !disabled,
              'border-red-500': erroredOperation?.id === step.id,
              'bg-white': !((recorderState?.stepId === step.id && !disabled) || selected),
              'border-blue-400 bg-gray-300 dark:border-blue-400':
                (recorderState?.stepId === step.id && !disabled) || selected,
              'opacity-60': step.skip,
            },
          )}
        >
          {/* Header */}
          <HoverCard
            position="right"
            // Keep dropdown flush with icon actions (no Mantine default padding)
            styles={{ dropdown: { padding: 0, border: 'none' } }}
            disabled={quickActionsDisabled}
            zIndex={1500}
            withinPortal
            offset={6}
          >
            <HoverCard.Target>
              <div>
                <div
                  className={`group flex w-full items-center justify-between space-x-2 rounded-md bg-[#fafafa] py-[10px] pr-3 pl-[6px] dark:bg-stone-800 ${
                    disabled ? 'cursor-not-allowed'
                    : !readonly ? 'cursor-pointer'
                    : ''
                  }`}
                >
                  <div className="flex w-full items-center text-left">
                    <StepStatusAndSelect
                      step={step}
                      idx={idx}
                      testContentId={testContentId}
                      selected={selected}
                      readonly={readonly}
                      setExpanded={setExpanded}
                      setPrevDesc={setPrevDesc}
                    />
                    {/* Step idx and icon */}
                    <div
                      className={clsx(
                        'flex items-center pr-0 text-sm font-medium',
                        disabled ? 'text-stone-700 dark:text-stone-400' : 'text-stone-600 dark:text-stone-400',
                      )}
                      ref={handleRef}
                    >
                      {idx !== undefined && (
                        <div
                          className={`relative flex h-5 w-5 items-center justify-center p-2 ${disabled || readonly ? 'cursor-not-allowed' : 'hover:cursor-grab'}`}
                        >
                          <div>{idx + 1}</div>
                        </div>
                      )}

                      {step.skip ?
                        <Tooltip label="Step will be skipped">
                          <Avatar
                            size="sm"
                            radius="xl"
                          >
                            <IconStrikethrough size={16} />
                          </Avatar>
                        </Tooltip>
                      : null}
                    </div>

                    {/* Step title */}
                    <div
                      className={clsx(
                        'dont-break-out ml-[6px] flex w-full flex-1 items-center space-x-2 text-[13px] font-semibold tracking-tight first-letter:capitalize',
                        disabled ? 'text-stone-700 dark:text-stone-400' : 'text-stone-800 dark:text-stone-300',
                      )}
                    >
                      {/* Step modes */}
                      {step.mode === 'STRICT' && (
                        <HoverCard
                          width={200}
                          shadow="md"
                          openDelay={150}
                        >
                          <HoverCard.Target>
                            <IconCircleKeyFilled
                              size={16}
                              className="mr-1 text-gray-500"
                            />
                          </HoverCard.Target>
                          <HoverCard.Dropdown>
                            <div className="flex flex-col gap-1 text-xs">
                              <b>Strict mode</b>
                              <div>No AI auto-healing</div>
                            </div>
                          </HoverCard.Dropdown>
                        </HoverCard>
                      )}
                      {/* Removed SingleOp branch for CODE_BLOCK since we now only support navigation */}
                      {step.mode === 'AUTH' && (
                        <Tooltip label="Authentication step">
                          <IconUser size={16} />
                        </Tooltip>
                      )}
                      {step.mode === 'AI_DYNAMIC' && (
                        <HoverCard
                          width={200}
                          shadow="md"
                          openDelay={150}
                        >
                          <HoverCard.Target>
                            <IconAIEdit className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                          </HoverCard.Target>
                          <HoverCard.Dropdown>
                            <div className="text-sm">
                              <b>AI-Generation mode:</b>
                              <br />
                              AI will generate actions for this step if being previewed. <br />
                            </div>
                          </HoverCard.Dropdown>
                        </HoverCard>
                      )}
                      {step.mode === 'AUTH' ?
                        <StepAccountDetails
                          stepId={step.id}
                          testContentId={testContentId}
                        />
                      : <StepTitle
                          step={step}
                          selected={selected}
                          readonly={readonly}
                          disabled={disabled}
                          setPrevDesc={setPrevDesc}
                        />
                      }
                    </div>
                  </div>
                  {/* Step mode and expand/collapse icon */}
                  <div className={clsx('flex flex-shrink-0 items-center space-x-[0.35rem]')}>
                    {!!prevDesc && (
                      <HoverCard
                        width={250}
                        shadow="md"
                        openDelay={150}
                      >
                        <HoverCard.Target>
                          <ActionIcon
                            className="text-gray-600"
                            size="xs"
                            variant="transparent"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateStep({ stepId: step.id, data: { description: prevDesc } });
                            }}
                          >
                            <IconHistory size={15} />
                          </ActionIcon>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                          <div className="flex flex-col gap-1 text-xs">
                            <b>Revert to previous description</b>
                            <Text
                              lineClamp={2}
                              fs="italic"
                              size="xs"
                            >
                              &quot;{prevDesc}&quot;
                            </Text>
                          </div>
                        </HoverCard.Dropdown>
                      </HoverCard>
                    )}
                    <ActionIcon
                      title={expanded ? 'Collapse' : 'Expand'}
                      size="xs"
                      variant="transparent"
                      color="gray"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((prev) => !prev);
                      }}
                    >
                      <IconChevronDown
                        className={clsx('transition-all duration-200 ease-in-out', {
                          'rotate-180': expanded,
                        })}
                        size={15}
                      />
                    </ActionIcon>

                    {idx !== undefined && (
                      <StepSettingsMenuButton
                        step={step}
                        testContentId={testContentId}
                        newStepOnMoveCursor={step.mode === 'AI_DYNAMIC'}
                        disabled={disabled}
                        isBeforeCursor={stepIsBeforeCursor}
                        readonly={readonly}
                      />
                    )}
                  </div>
                </div>
              </div>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <StepPreviewButton
                step={step}
                show={true}
                showScrollToOption={Boolean(stepSearchBarKeyword)}
                disabled={quickActionsDisabled}
                testContentId={testContentId}
              />
            </HoverCard.Dropdown>
          </HoverCard>
          {/* Content */}
          {/* Show the operation list only when we are not promoting a single navigation op */}
          <CollapseUnmount in={expanded}>
            <div className="mx-auto w-[100%] border-t-2 dark:border-gray-600" />
            <div>
              <OperationListStep
                step={step}
                testContentId={testContentId}
                readonly={readonly || disabled}
                disabled={disabled}
                stepIdx={idx}
                handleOperationClick={handleOperationClick}
                autoHealConfig={autoHealConfig}
                autoHealCycleLimit={autoHealCycleLimit}
              />
            </div>
          </CollapseUnmount>
        </div>
      </div>
    </div>
  );
}
